/**
 * CustomMainHeader — a barra do topo.
 *
 * ORIGEM: esqueleto do tema AdminUX; **o comportamento é nosso** — o sino de
 * notificações, o claro/escuro, o atalho da ajuda e o menu do usuário.
 * Original em `theme/adminux-react/src/components/MainHeader.tsx`.
 */
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '@/auth/auth-context';
import CustomHeaderNotificacoes from './CustomHeaderNotificacoes';
import { IconMenu, IconMoon, IconQuestion, IconRefresh, IconSun } from './icons';
import { CustomMarcaCasaANome, CustomMarcaCasaASimbolo } from './CustomMarcasCasaA';
import { instalarAgora } from '@/lib/instalacao';
import { usarInstalacao } from '@/lib/usar-instalacao';
/* O corte mora num lugar só desde 25/08 — ver `lib/tela.ts`. */
import { LARGURA_DESKTOP } from '@/lib/tela';

const CustomMainHeader: React.FC = () => {
  const { darkMode, setDarkMode, sidebarLayout, setSidebarLayout } = useTheme();
  const recolhido = sidebarLayout === 'iconic';
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  // Instalar como app. A faixa do início é dispensável e some para sempre; este
  // item é a entrada que fica — e é a ÚNICA no computador, onde não há faixa
  // quando o Chrome não oferece o convite.
  const { ambiente, temPrompt } = usarInstalacao();
  const podeInstalar =
    !!ambiente && !ambiente.instalado && (temPrompt || ambiente.aparelho !== 'desktop');

  useEffect(() => {
    openSidebar();
    window.addEventListener('resize', openSidebar);
    return () => window.removeEventListener('resize', openSidebar);
  }, []);

  /**
   * O botão do menu faz coisas diferentes conforme o espaço disponível:
   *
   * - Desktop: RECOLHE o painel para 60px (modo `iconic` do tema) — só os
   *   ícones, com o rótulo aparecendo no hover. O estado persiste em
   *   localStorage pelo ThemeContext, então continua recolhido na próxima vez.
   * - Celular: não há espaço para meio-termo, então esconde/mostra o painel
   *   inteiro (classes `sidebar-open`/`sidebar-close` no <body>).
   */
  const toggleSidebar = () => {
    const body = document.body;
    if (window.innerWidth >= LARGURA_DESKTOP) {
      setSidebarLayout(recolhido ? 'standard' : 'iconic');
      return;
    }
    if (body.classList.contains('sidebar-close')) {
      body.classList.remove('sidebar-close');
      body.classList.add('sidebar-open');
    } else {
      body.classList.remove('sidebar-open');
      body.classList.add('sidebar-close');
    }
  };

  const openSidebar = () => {
    const body = document.body;
    if (window.innerWidth >= LARGURA_DESKTOP) {
      body.classList.add('sidebar-open');
      body.classList.remove('sidebar-close');
    } else {
      body.classList.add('sidebar-close');
      body.classList.remove('sidebar-open');
    }
  };

  const sair = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Avatar (foto do usuário ou ícone), em vários tamanhos.
  const avatar = (tam: number) =>
    usuario?.foto ? (
      <figure
        className={`avatar avatar-${tam} rounded-circle coverimg align-middle mb-0`}
      >
        <img src={usuario.foto} alt="" />
      </figure>
    ) : (
      <span
        className={`avatar avatar-${tam} rounded-circle bg-theme-1-subtle text-theme-1 d-flex align-items-center justify-content-center`}
      >
        <i className="bi bi-person"></i>
      </span>
    );

  return (
    <nav className="navbar navbar-expand-lg fixed-top">
      <div className="container-fluid">
        {/* toggle do menu: recolhe no desktop, esconde/mostra no celular */}
        <button
          className="btn btn-link btn-square sidebar-toggler"
          type="button"
          onClick={toggleSidebar}
          title={recolhido ? 'Expandir o menu' : 'Recolher o menu'}
          aria-label={recolhido ? 'Expandir o menu' : 'Recolher o menu'}
        >
          <IconMenu className="sidebar-svg" size={26} />
        </button>

        {/* marca: ícone redondo + nome + descrição */}
        <Link className="navbar-brand" to="/inicio">
          {/* O SÍMBOLO É VETOR desde 26/08/2026 (era `casaa-icon.png`, círculo
              preto que continuava preto sobre o header azul). Vazado e
              `currentColor` — ver `CustomMarcasCasaA`. */}
          {/* Sem tamanho aqui de propósito: quem dimensiona é a classe
              `.casaa-icon`, na folha de estilo. */}
          <CustomMarcaCasaASimbolo className="casaa-icon" />
          <div>
            <CustomMarcaCasaANome className="casaa-wordmark casaa-wordmark-alta" />
          </div>
        </Link>

        {/* ações à direita: ajuda, tema (sol/lua), notificações (sino), perfil.
            Container com gap uniforme; todos os gatilhos usam btn-square (38px). */}
        <div className="ms-auto d-flex align-items-center gap-1">
          {/* RECARREGAR. Existe por causa do modo aplicativo: sem barra de
              endereço não há botão de recarregar, e no celular nem atalho de
              teclado — quem precisa atualizar fica sem gesto nenhum. É um
              reload puro: o `no-cache` do nginx no index.html já garante que
              venha a versão nova (ver CustomAvisoNovaVersao). Não limpa cache
              de nada. */}
          <button
            className="btn btn-link btn-square btn-link-header"
            onClick={() => window.location.reload()}
            title="Recarregar a página"
            aria-label="Recarregar a página"
          >
            <IconRefresh />
          </button>

          {/* ajuda: leva à Central de Ajuda */}
          <button
            className="btn btn-link btn-square btn-link-header"
            onClick={() => navigate('/ajuda')}
            title="Ajuda"
          >
            <IconQuestion />
          </button>

          {/* dark mode: mostra o estado ATUAL (sol = claro, lua = escuro) */}
          <button
            className="btn btn-link btn-square btn-link-header"
            onClick={() => setDarkMode(!darkMode)}
            title={darkMode ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          >
            {darkMode ? <IconMoon /> : <IconSun />}
          </button>

          {/* notificações (sino) */}
          <CustomHeaderNotificacoes />

          {/* perfil */}
          <div className="dropdown d-inline-block">
            <a
              className="dropdown-toggle btn btn-link btn-square btn-link-header style-none no-caret"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              role="button"
            >
              {avatar(28)}
            </a>
            <div className="dropdown-menu dropdown-menu-end width-300 px-0 pt-0">
              {/* cabeçalho: avatar + nome + perfil */}
              <div className="p-3 rounded mb-2">
                <div className="row gx-3">
                  <div className="col-auto">{avatar(50)}</div>
                  <div className="col align-self-center">
                    <h6 className="mb-0">{usuario?.nome ?? '—'}</h6>
                    <p className="small text-secondary mb-0">
                      {usuario?.perfil_nome ?? usuario?.perfil ?? ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* itens */}
              <div className="px-2">
                <a
                  className="dropdown-item"
                  role="button"
                  onClick={() => navigate('/pessoal/acesso')}
                >
                  <span className="avatar avatar-30 rounded-circle me-1">
                    <i className="bi bi-person"></i>
                  </span>
                  Meu perfil
                </a>
                <a
                  className="dropdown-item"
                  role="button"
                  onClick={() => navigate('/meus-dados')}
                >
                  <span className="avatar avatar-30 rounded-circle me-1">
                    <i className="bi bi-card-text"></i>
                  </span>
                  Meus dados
                </a>
                <a
                  className="dropdown-item"
                  role="button"
                  onClick={() => navigate('/minha-area/aparencia')}
                >
                  <span className="avatar avatar-30 rounded-circle me-1">
                    <i className="bi bi-palette"></i>
                  </span>
                  Aparência
                </a>
                {podeInstalar && (
                  <a
                    className="dropdown-item"
                    role="button"
                    onClick={async () => {
                      // Com o convite do Chrome em mãos, instala aqui mesmo; sem
                      // ele (iPhone, Firefox), a tela explica o caminho.
                      if (temPrompt && (await instalarAgora())) return;
                      navigate('/public/instalar');
                    }}
                  >
                    <span className="avatar avatar-30 rounded-circle me-1">
                      <i className="bi bi-download"></i>
                    </span>
                    Instalar o app
                  </a>
                )}
                <a className="dropdown-item theme-red" role="button" onClick={sair}>
                  <span className="avatar avatar-30 rounded-circle me-1">
                    <i className="bi bi-power"></i>
                  </span>
                  Sair
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default CustomMainHeader;
